import { ISceneInfo } from '@/editor/types';
import { Intersection, Object3D, Plane, Raycaster, Vector2, Vector3 } from 'three';

export class MouseCoordinateConverter {
  private _raycaster: Raycaster;
  private _mouse: Vector2;
  private _sceneInfo: ISceneInfo;
  private _plane: Plane;

  constructor(sceneInfo: ISceneInfo) {
    this._raycaster = new Raycaster();
    this._mouse = new Vector2();
    this._sceneInfo = sceneInfo;

    const planeNormal: Vector3 = new Vector3(0, 0, 1); // 默认XY平面
    const planeConstant: number = 0; // 默认Z=0平面
    // 创建平面
    this._plane = new Plane(planeNormal, planeConstant);
  }

  /**
   * 将鼠标事件坐标转换为标准化设备坐标
   */
  private getNormalizedMouseCoordinates(event: MouseEvent, canvas: HTMLCanvasElement): Vector2 {
    const rect = canvas.getBoundingClientRect();

    // 计算鼠标在canvas上的相对位置
    const x = ((event.clientX - rect.left) / canvas.clientWidth) * 2 - 1;
    const y = -((event.clientY - rect.top) / canvas.clientHeight) * 2 + 1;

    return new Vector2(x, y);
  }

  /**
   * 方法1: 通过射线投射获取与物体的交点
   * @returns 交点坐标数组
   */
  public getIntersectionPoints(event: MouseEvent, canvas: HTMLCanvasElement, objects?: Object3D[]): Intersection[] {
    this._mouse = this.getNormalizedMouseCoordinates(event, canvas);

    // 更新射线投射器
    this._raycaster.setFromCamera(this._mouse, this._sceneInfo.camera);

    // 如果指定了对象数组，则只检测这些对象
    const intersectObjects = objects || this._sceneInfo.scene.children;

    // 获取所有交点
    const intersects = this._raycaster.intersectObjects(intersectObjects, true);

    return intersects;
  }

  /**
   * 方法2: 获取第一个交点坐标
   */
  public getFirstIntersectionPoint(event: MouseEvent, canvas: HTMLCanvasElement, objects?: Object3D[]): Vector3 | null {
    const intersects = this.getIntersectionPoints(event, canvas, objects);

    if (intersects.length > 0) {
      return intersects[0].point;
    }

    return null;
  }

  /**
   * 方法3: 获取鼠标指向的3D空间中的点（在指定平面上）
   * @param planeNormal 平面法向量
   * @param planeConstant 平面常数
   */
  public getPointOnPlane(event: MouseEvent, canvas: HTMLCanvasElement): Vector3 | null {
    this._mouse = this.getNormalizedMouseCoordinates(event, canvas);
    this._raycaster.setFromCamera(this._mouse, this._sceneInfo.camera);

    // 计算射线与平面的交点
    const intersectionPoint = new Vector3();
    const result = this._raycaster.ray.intersectPlane(this._plane, intersectionPoint);

    return result ? intersectionPoint : null;
  }

  /**
   * 方法4: 获取鼠标在指定距离处的3D坐标
   */
  public getPointAtDistance(event: MouseEvent, canvas: HTMLCanvasElement, distance: number = 10): Vector3 {
    this._mouse = this.getNormalizedMouseCoordinates(event, canvas);
    this._raycaster.setFromCamera(this._mouse, this._sceneInfo.camera);

    // 计算射线上指定距离处的点
    const point = new Vector3();
    this._raycaster.ray.at(distance, point);

    return point;
  }
}
